/**
 * Recurring schedule-event date maths — pure, no Firebase.
 *
 * A recurring series is a template doc (`isTemplate: true` + `recurringRule`)
 * plus one instance doc per occurrence, minted at the deterministic id
 * `${templateId}_${YYYY-MM-DD}`. These helpers answer "which dates does this
 * rule produce" and "which existing instances no longer belong" so the service
 * layer can keep the grid matching what the modal last said.
 *
 * All dates are local `YYYY-MM-DD` strings. Never `toISOString()` — it returns
 * yesterday in AEST.
 */

import type { RecurringRule } from '@/types/schema';

/** How far ahead an edit or create writes occurrences; later weeks fill lazily. */
export const GENERATION_MONTHS = 3;

const DATE_SUFFIX = /^\d{4}-\d{2}-\d{2}$/;

/** Format a Date to YYYY-MM-DD (local timezone) */
export function formatDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parse YYYY-MM-DD to Date (local time) */
export function parseDateStr(str: string): Date {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Day-of-week index in the rule's convention (0=Mon..4=Fri, 5=Sat, 6=Sun) */
export function getDayIndex(date: Date): number {
  const jsDay = date.getDay(); // 0=Sun..6=Sat
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** The days a rule repeats on. A 'daily' rule means every weekday. */
export function ruleDays(rule: RecurringRule): number[] {
  return rule.frequency === 'daily' ? [0, 1, 2, 3, 4] : (rule.dayOfWeek ?? []);
}

/** Whether `dateStr` is an occurrence of the rule (days, start, end, exceptions). */
export function ruleMatchesDate(rule: RecurringRule, dateStr: string): boolean {
  if (dateStr < rule.startDate) return false;
  if (rule.endDate && dateStr > rule.endDate) return false;
  if (rule.exceptions?.includes(dateStr)) return false;
  return ruleDays(rule).includes(getDayIndex(parseDateStr(dateStr)));
}

/** Every occurrence date of the rule between `fromDate` and `toDate`, inclusive. */
export function datesForRule(rule: RecurringRule, fromDate: string, toDate: string): string[] {
  const dates: string[] = [];
  const current = parseDateStr(fromDate);
  const to = parseDateStr(toDate);
  while (current <= to) {
    const dateStr = formatDateStr(current);
    if (rule.endDate && dateStr > rule.endDate) break;
    if (ruleMatchesDate(rule, dateStr)) dates.push(dateStr);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

/**
 * The date a deterministic instance id was minted for, or null for an id the
 * generator didn't produce (legacy random ids).
 */
export function generatedDateOf(templateId: string, instanceId: string): string | null {
  const prefix = `${templateId}_`;
  if (!instanceId.startsWith(prefix)) return null;
  const suffix = instanceId.slice(prefix.length);
  return DATE_SUFFIX.test(suffix) ? suffix : null;
}

/** An occurrence the operator moved to another day (its id still names the old one). */
export function isMovedOccurrence(templateId: string, instance: { id: string; date: string }): boolean {
  const generated = generatedDateOf(templateId, instance.id);
  return generated !== null && generated !== instance.date;
}

/**
 * Instances that no longer belong to the series after its rule changed.
 *
 * Bounded on purpose — this list is deleted:
 *   - only instances dated today or later (history is never rewritten);
 *   - never an occurrence the operator moved by hand (it is theirs now);
 *   - only dates the rule no longer produces.
 */
export function instancesToRemove(
  templateId: string,
  instances: Array<{ id: string; date: string }>,
  rule: RecurringRule,
  todayStr: string,
): string[] {
  return instances
    .filter((i) => i.date >= todayStr)
    .filter((i) => !isMovedOccurrence(templateId, i))
    .filter((i) => !ruleMatchesDate(rule, i.date))
    .map((i) => i.id);
}

/** Add whole months to a YYYY-MM-DD date. */
export function addMonths(dateStr: string, months: number): string {
  const d = parseDateStr(dateStr);
  d.setMonth(d.getMonth() + months);
  return formatDateStr(d);
}

/**
 * The window an edit regenerates: from today (or the start, if later) for
 * GENERATION_MONTHS, cut short by the rule's end date. Null when the series
 * has already ended.
 */
export function generationWindow(rule: RecurringRule, todayStr: string): { from: string; to: string } | null {
  const from = rule.startDate > todayStr ? rule.startDate : todayStr;
  const ahead = addMonths(from, GENERATION_MONTHS);
  const to = rule.endDate && rule.endDate < ahead ? rule.endDate : ahead;
  return to < from ? null : { from, to };
}

/** Whether two rules describe the same recurrence (exceptions aside). */
export function sameRecurrence(a: RecurringRule, b: RecurringRule): boolean {
  const days = (r: RecurringRule) => [...ruleDays(r)].sort((x, y) => x - y).join(',');
  return days(a) === days(b) && a.startDate === b.startDate && (a.endDate ?? '') === (b.endDate ?? '');
}
